import { User, CreateUserDTO, UpdateUserDTO, ApiResponse } from '../types/User';
import { isBirthday } from '../utils/validation';

// Utility to generate a unique ID
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments that don't support crypto.randomUUID
  return Math.random().toString(36).substring(2, 11);
};

// Mock database
let users: User[] = [
  {
    id: '1',
    rut: '12.345.678-5',
    nombre: 'Juan Pérez',
    fechaNacimiento: '1990-05-14',
    cantidadHijos: 2,
    correoElectronico: 'juan.perez@email.com',
    telefonos: ['+56912345678'],
    direcciones: ['Av. Las Condes 1234, Santiago'],
    fechaCreacion: '2024-01-01T00:00:00.000Z',
    fechaActualizacion: '2024-01-02T00:00:00.000Z',
  },
  {
    id: '2',
    rut: '15.234.567-4',
    nombre: 'María González',
    fechaNacimiento: '1985-03-21',
    cantidadHijos: 1,
    correoElectronico: 'maria.gonzalez@email.com',
    telefonos: ['+56987654321', '+56223456789'],
    direcciones: ['Calle Principal 567, Valparaíso', 'Av. Libertad 890, Viña del Mar'],
    fechaCreacion: '2024-01-01T00:00:00.000Z',
    fechaActualizacion: '2024-01-02T00:00:00.000Z',
  },
];

// Simulate an API response delay
const simulateDelay = (ms: number = 500): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export class UserService {
  static async getAllUsers(): Promise<ApiResponse<User[]>> {
    await simulateDelay();

    try {
      return {
        success: true,
        data: [...users],
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error fetching users',
      };
    }
  }

  static async getUserById(id: string): Promise<ApiResponse<User>> {
    await simulateDelay();

    try {
      const user = users.find((u) => u.id === id);
      if (!user) {
        return {
          success: false,
          message: 'User not found',
        };
      }

      return {
        success: true,
        data: user,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error fetching user',
      };
    }
  }

  static async createUser(userData: CreateUserDTO): Promise<ApiResponse<User>> {
    await simulateDelay();

    try {
      // Normalize RUT by removing dots and dashes for comparison
      const normalizeRut = (rut: string) => rut.replace(/[.-]/g, '').toLowerCase();

      const newRutNormalized = normalizeRut(userData.rut);

      const existingUser = users.find((u) => normalizeRut(u.rut) === newRutNormalized);
      if (existingUser) {
        return {
          success: false,
          message: 'RUT is already registered',
        };
      }

      const newUser: User = {
        ...userData,
        id: generateId(),
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString(),
      };

      users.push(newUser);

      return {
        success: true,
        data: newUser,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error creating user',
      };
    }
  }

  static async updateUser(id: string, userData: UpdateUserDTO): Promise<ApiResponse<User>> {
    await simulateDelay();

    try {
      const userIndex = users.findIndex((u) => u.id === id);
      if (userIndex === -1) {
        return {
          success: false,
          message: 'User not found',
        };
      }

      const currentUser = users[userIndex];

      const allowedFields = [
        'nombre',
        'fechaNacimiento',
        'cantidadHijos',
        'correoElectronico',
        'telefonos',
        'direcciones',
      ] as const;

      const filteredUserData = allowedFields.reduce((acc, key) => {
        if (key in userData) {
          (acc as any)[key] = (userData as any)[key];
        }
        return acc;
      }, {} as Partial<UpdateUserDTO>);

      const updatedUser: User = {
        ...currentUser,
        ...filteredUserData,
        rut: currentUser.rut,
        fechaActualizacion: new Date().toISOString(),
      };

      users[userIndex] = updatedUser;

      return {
        success: true,
        data: updatedUser,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error updating user',
      };
    }
  }

  static async deleteUser(id: string): Promise<ApiResponse<void>> {
    await simulateDelay();

    try {
      const userIndex = users.findIndex((u) => u.id === id);
      if (userIndex === -1) {
        return {
          success: false,
          message: 'User not found',
        };
      }

      const user = users[userIndex];

      // Check if the user has a birthday today
      if (isBirthday(user.fechaNacimiento)) {
        return {
          success: false,
          message: 'Cannot delete a user who has a birthday today',
        };
      }

      users.splice(userIndex, 1);

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error deleting user',
      };
    }
  }

  static async getUsersCount(): Promise<ApiResponse<number>> {
    await simulateDelay(200);

    try {
      return {
        success: true,
        data: users.length,
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error fetching users count',
      };
    }
  }
}

// Export for testing
export const __testUtils__ = {
  resetUsers: () => {
    users = [];
  },
  setUsers: (newUsers: User[]) => {
    users = [...newUsers];
  },
  getUsers: () => [...users],
};
